import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Card } from '@altermaind/web/ui';
import { usePaginatedTable } from '@/_shared/components';
import { TableHeader } from '@/_shared/components/TableHeader';
import { useDialog } from '@/_shared/hooks';
import {
  ImportedEmployer,
  getEmployeesImportCall,
  postEmployeesImportsTemplateCall,
  getEmployeesImportsStatsCall,
} from '@/people/state';
import { PAGE_SIZE_IMPORTED_EMPLOYEES_IN_DIALOG } from '@/people/state/constants';
import { FilterImportedProfiles } from './FilterImportedProfiles';
import { TableImportedProfiles } from './TableImportedProfiles';
import { DialogEditImportedProfilesPayload } from './types';

export const DialogEditImportedProfiles: FC = () => {
  const t = useTranslations('configuration.hr.verifyData.dialogEditImportedProfiles');
  const {
    closeDialog,
    Dialog,
    isOpen,
    payload: dialogFiltersPayload,
  } = useDialog<DialogEditImportedProfilesPayload>('uploadCompanyDataEditImportedProfiles');
  const [selectedProfile, setSelectedProfile] = useState<ImportedEmployer | undefined>();
  const { jobId } = postEmployeesImportsTemplateCall.hook();
  const {
    loading: loadingGetEmployeesImport,
    error: errorGetEmployeesImport,
    success: successGetEmployeesImport,
    execute: executeGetEmployeesImport,
    reset: resetGetEmployeesImport,
    totalItems,
    data,
  } = getEmployeesImportCall.hook();
  const { execute: executeGetEmployeesImportsStats } = getEmployeesImportsStatsCall.hook();

  const [displayedData, setDisplayedData] = useState<ImportedEmployer[]>(data || []);

  const paginedProps = usePaginatedTable({
    rowData: displayedData,
    pageSize: PAGE_SIZE_IMPORTED_EMPLOYEES_IN_DIALOG,
    totalItems,
  });

  const onDialogClose = useCallback(() => {
    closeDialog();
    if (jobId) {
      executeGetEmployeesImportsStats({
        jobId,
      });
      executeGetEmployeesImport({
        jobId,
        start: 0,
        length: PAGE_SIZE_IMPORTED_EMPLOYEES_IN_DIALOG,
      });
    }
  }, [closeDialog, executeGetEmployeesImport, executeGetEmployeesImportsStats, jobId]);

  useEffect(() => {
    if (
      !loadingGetEmployeesImport &&
      !successGetEmployeesImport &&
      !errorGetEmployeesImport &&
      jobId
    ) {
      executeGetEmployeesImport({
        jobId,
        start: 0,
        length: PAGE_SIZE_IMPORTED_EMPLOYEES_IN_DIALOG,
      });
    }

    return () => {
      resetGetEmployeesImport();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (successGetEmployeesImport) {
      setDisplayedData(data || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successGetEmployeesImport]);

  useEffect(() => {
    if (jobId) {
      executeGetEmployeesImport({
        jobId,
        start: 0,
        length: PAGE_SIZE_IMPORTED_EMPLOYEES_IN_DIALOG,
        issueFields: dialogFiltersPayload?.filters?.issueFields || undefined,
      });
    }
  }, [dialogFiltersPayload, executeGetEmployeesImport, jobId]);

  return (
    <Dialog open={isOpen} title={undefined} onClose={onDialogClose} closeButton maxWidth='95%'>
      <Box display='flex' mx='l' flexDirection='column' px='s' flex={1} width='100%'>
        <Typography variant='h3' color='primary/text'>
          {t('title')}
        </Typography>
      </Box>
      <Box display='flex' mt='xs' flexDirection='column' px='s'>
        <Typography variant='p2/light' color='grey/light'>
          {t('description')}
        </Typography>
      </Box>

      <Card maxWidth='100%' width='100%'>
        <TableHeader filtersComponent={<FilterImportedProfiles />} />
        <TableImportedProfiles
          selectedProfile={selectedProfile}
          setSelectedProfile={setSelectedProfile}
          paginedProps={paginedProps}
          setDisplayedData={setDisplayedData}
          pageSize={PAGE_SIZE_IMPORTED_EMPLOYEES_IN_DIALOG}
        />
      </Card>
    </Dialog>
  );
};
